"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Pencil, Save } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { AddWidgetModal } from "@/components/dashboard/AddWidgetModal";
import { DashboardEmptyState } from "@/components/dashboard/DashboardEmptyState";
import { DashboardGrid } from "@/components/dashboard/DashboardGrid";
import { DashboardRuntimeProvider } from "@/components/dashboard/DashboardRuntimeContext";
import { DashboardTelemetryBatchProvider } from "@/components/dashboard/DashboardTelemetryBatchProvider";
import { DashboardToolbar } from "@/components/dashboard/DashboardToolbar";
import { WidgetConfigPanel } from "@/components/dashboard/WidgetConfigPanel";
import { useAppDialogs } from "@/components/dialogs/app-dialog-provider";
import { Card, Button } from "@/components/ui-system";
import { DashboardBuilderProvider, useDashboardBuilderOptional } from "@/contexts/DashboardBuilderContext";
import { getActiveAlertSummary } from "@/lib/api/endpoints/alerts";
import { getDashboard, listDashboardWidgets, updateDashboard } from "@/lib/api/endpoints/dashboards";
import { listUsers } from "@/lib/api/endpoints/users";
import type { DashboardDto } from "@/lib/api/types";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { useAuthStore } from "@/modules/auth/store";

import type { DashboardEditorState } from "../components/dashboard-management";
import {
  buildEditEditorState,
  canEditDashboardForUser,
  DashboardMetaModal,
  filterAssignableUsers,
} from "../components/dashboard-management";
import { HmiDashboardPage } from "../hmi/components/hmi-dashboard-page";
import { getDashboardLayoutKind } from "../hmi/utils/layout-kind";

function DashboardCanvasHeader({
  dashboard,
  canEditDashboard,
  onBack,
  onEditDetails,
}: {
  dashboard: DashboardDto;
  canEditDashboard: boolean;
  onBack: () => void;
  onEditDetails: () => void;
}) {
  const builder = useDashboardBuilderOptional();
  const dialogs = useAppDialogs();

  const saveDisabled = !builder || !builder.editMode || !builder.isDirty || builder.saving;
  const handleBackClick = async () => {
    if (
      builder?.isDirty &&
      !(await dialogs.confirm({
        title: "Leave canvas?",
        description: "Unsaved dashboard changes will be discarded if you go back now.",
        confirmLabel: "Leave without saving",
        tone: "danger",
      }))
    ) {
      return;
    }
    onBack();
  };

  return (
    <div className="border-b border-card-border bg-surface-primary px-ds-6 py-ds-5">
      <div className="flex flex-wrap items-start justify-between gap-ds-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-ds-2">
            <Button type="button" variant="ghost" size="sm" className="gap-ds-2" onClick={() => void handleBackClick()}>
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Back
            </Button>
            <span
              className={`rounded-full border px-ds-2 py-[2px] text-[11px] font-semibold uppercase tracking-[0.08em] ${
                dashboard.is_global
                  ? "border-success/30 bg-success/10 text-success"
                  : "border-warning/30 bg-warning/10 text-warning"
              }`}
            >
              {dashboard.is_global ? "Global" : "Restricted"}
            </span>
            {builder?.isDirty ? (
              <span className="rounded-control border border-warning/30 bg-warning/10 px-ds-2 py-[2px] text-micro font-semibold uppercase tracking-[0.08em] text-warning">
                Unsaved changes
              </span>
            ) : null}
          </div>
          <h1 className="mt-ds-3 truncate text-display tracking-tight text-foreground">{dashboard.name}</h1>
          <p className="mt-ds-1 text-caption text-muted">
            {dashboard.description?.trim() || "Adjust widgets, layout, and visibility from this dedicated canvas."}
          </p>
          {builder?.saveError ? (
            <p className="mt-ds-2 text-micro text-error" role="alert">
              {builder.saveError}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-ds-2">
          {canEditDashboard ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="!px-2"
              onClick={onEditDetails}
              title="Edit details"
              aria-label="Edit details"
            >
              <Pencil className="h-4 w-4" aria-hidden />
            </Button>
          ) : null}
          {canEditDashboard && builder ? (
            <Button
              type="button"
              variant="primary"
              size="sm"
              className="gap-ds-2"
              disabled={saveDisabled}
              onClick={() => void builder.saveAll()}
            >
              {builder.saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Save className="h-4 w-4" aria-hidden />}
              {builder.saving ? "Saving..." : "Save changes"}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function DashboardCanvasContent({
  tenantId,
  dashboard,
  widgets,
  activeAlertFingerprints,
  canEditDashboard,
  onBack,
  onEditDetails,
}: {
  tenantId: string;
  dashboard: DashboardDto;
  widgets: Awaited<ReturnType<typeof listDashboardWidgets>>;
  activeAlertFingerprints: Set<string>;
  canEditDashboard: boolean;
  onBack: () => void;
  onEditDetails: () => void;
}) {
  if (canEditDashboard) {
    return (
      <DashboardBuilderProvider dashboardId={dashboard.id} widgets={widgets} initialEditMode>
        <DashboardRuntimeProvider activeAlertFingerprints={activeAlertFingerprints}>
          <DashboardTelemetryBatchProvider tenantId={tenantId} dashboardId={dashboard.id} widgets={widgets}>
            <div className="flex h-full min-h-0 flex-col bg-surface-canvas text-foreground">
              <DashboardCanvasHeader
                dashboard={dashboard}
                canEditDashboard={canEditDashboard}
                onBack={onBack}
                onEditDetails={onEditDetails}
              />
              <main className="min-h-0 flex-1 overflow-y-auto px-ds-6 py-ds-5">
                <DashboardToolbar
                  dashboardName={dashboard.name}
                  showEditToggle
                  showAddWidget
                  showSaveActions={false}
                />
                {widgets.length === 0 ? <DashboardEmptyState /> : <DashboardGrid widgets={widgets} />}
                <WidgetConfigPanel tenantId={tenantId} />
                <AddWidgetModal tenantId={tenantId} dashboardId={dashboard.id} />
              </main>
            </div>
          </DashboardTelemetryBatchProvider>
        </DashboardRuntimeProvider>
      </DashboardBuilderProvider>
    );
  }

  return (
    <DashboardRuntimeProvider activeAlertFingerprints={activeAlertFingerprints}>
      <DashboardTelemetryBatchProvider tenantId={tenantId} dashboardId={dashboard.id} widgets={widgets}>
          <div className="flex h-full min-h-0 flex-col bg-surface-canvas text-foreground">
          <DashboardCanvasHeader
            dashboard={dashboard}
            canEditDashboard={canEditDashboard}
            onBack={onBack}
            onEditDetails={onEditDetails}
          />
          <main className="min-h-0 flex-1 overflow-y-auto px-ds-6 py-ds-5">
            <DashboardToolbar
              dashboardName={dashboard.name}
              showEditToggle={false}
              showAddWidget={false}
              showSaveActions={false}
            />
            {widgets.length === 0 ? (
              <Card className="flex flex-col items-center justify-center gap-ds-3 border-dashed border-card-border bg-surface-secondary p-ds-10 text-center">
                <p className="text-title text-foreground">No widgets yet</p>
                <p className="max-w-md text-caption text-muted">
                  This dashboard has been published, but no widgets have been added to it yet.
                </p>
              </Card>
            ) : (
              <DashboardGrid widgets={widgets} />
            )}
          </main>
        </div>
      </DashboardTelemetryBatchProvider>
    </DashboardRuntimeProvider>
  );
}

export function DashboardCanvasPage({ dashboardId }: { dashboardId: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const tenantId = useAuthStore((s) => s.tenantId);
  const user = useAuthStore((s) => s.user);
  const { permissionMap } = useEffectivePermissions();
  const canView = hasModulePermission(user, permissionMap, "dashboards", "view");
  const canEdit = hasModulePermission(user, permissionMap, "dashboards", "edit");
  const [editor, setEditor] = useState<DashboardEditorState>({ open: false });
  const [editorError, setEditorError] = useState<string | null>(null);

  const dashboardQuery = useQuery({
    queryKey: ["dashboard", dashboardId],
    queryFn: () => getDashboard(dashboardId),
    enabled: Boolean(dashboardId && canView),
    staleTime: 30_000,
  });

  const usersQuery = useQuery({
    queryKey: ["users", tenantId ?? "", "dashboard-assignments"],
    queryFn: async () => (await listUsers(tenantId)).items,
    enabled: Boolean(tenantId && canEdit),
    staleTime: 30_000,
  });

  const dashboard = dashboardQuery.data ?? null;
  const canEditDashboard = Boolean(dashboard && canEdit && canEditDashboardForUser(user, dashboard));

  const assignableUsers = useMemo(() => {
    return [...filterAssignableUsers(user, usersQuery.data ?? [])].sort((a, b) => {
      const nameA = (a.name ?? a.email).toLowerCase();
      const nameB = (b.name ?? b.email).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [user, usersQuery.data]);

  const widgetsQuery = useQuery({
    queryKey: ["dashboard-widgets", dashboardId],
    queryFn: () => listDashboardWidgets(dashboardId),
    enabled: Boolean(dashboard && getDashboardLayoutKind(dashboard.layout) === "classic"),
    staleTime: 30_000,
  });

  const activeAlertSummaryQuery = useQuery({
    queryKey: ["alerts-summary-active", tenantId ?? ""],
    queryFn: () => getActiveAlertSummary(tenantId!),
    enabled: Boolean(tenantId && dashboard),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });

  const activeAlertFingerprints = useMemo(
    () => new Set(activeAlertSummaryQuery.data?.fingerprints ?? []),
    [activeAlertSummaryQuery.data?.fingerprints],
  );

  const updateMutation = useMutation({
    mutationFn: async (payload: {
      dashboardId: string;
      name: string;
      description: string;
      isGlobal: boolean;
      assignedUserIds: string[];
    }) => {
      return updateDashboard(payload.dashboardId, {
        name: payload.name.trim(),
        description: payload.description.trim() || null,
        is_global: payload.isGlobal,
        assigned_user_ids: payload.isGlobal ? [] : payload.assignedUserIds,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["dashboard", dashboardId] }),
        qc.invalidateQueries({ queryKey: ["dashboards", tenantId ?? ""] }),
      ]);
      setEditor({ open: false });
      setEditorError(null);
    },
  });

  const handleBack = () => {
    navigate("/dashboards");
  };

  const submitEditor = async () => {
    if (!editor.open) {
      return;
    }
    const name = editor.name.trim();
    if (!name) {
      setEditorError("Dashboard name is required.");
      return;
    }
    if (!editor.isGlobal && editor.assignedUserIds.length === 0) {
      setEditorError("Select at least one user for a restricted dashboard.");
      return;
    }
    setEditorError(null);
    try {
      await updateMutation.mutateAsync({
        dashboardId: editor.dashboardId!,
        name,
        description: editor.description,
        isGlobal: editor.isGlobal,
        assignedUserIds: editor.assignedUserIds,
      });
    } catch (error) {
      setEditorError(error instanceof Error ? error.message : "Could not save dashboard.");
    }
  };

  if (!tenantId) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-lg flex-col items-center justify-center gap-ds-4 p-ds-8 text-center">
        <Card className="w-full p-ds-8">
          <p className="text-body text-muted">
            Your session has no active workspace. Pick one from the header selector or sign in with a workspace-enabled account.
          </p>
          <Link to="/login" className="btn btn-primary btn-sm mt-ds-4 !no-underline">
            Sign in
          </Link>
        </Card>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center gap-ds-4 p-ds-8 text-center">
        <Card className="w-full p-ds-8">
          <p className="text-title text-foreground">Dashboard access is not enabled for your role</p>
          <p className="mt-ds-2 text-body text-muted">
            Your current permissions do not include access to the dashboards workspace.
          </p>
          <Link to="/overview" className="btn btn-secondary btn-sm mt-ds-4 !no-underline">
            Open overview
          </Link>
        </Card>
      </div>
    );
  }

  if (dashboardQuery.isLoading) {
    return (
      <div className="flex min-h-[24rem] items-center justify-center gap-ds-2 text-caption text-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading dashboard...
      </div>
    );
  }

  if (dashboardQuery.isError || !dashboard) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center gap-ds-4 p-ds-8 text-center">
        <Card className="w-full p-ds-8">
          <p className="text-title text-foreground">Dashboard not available</p>
          <p className="mt-ds-2 text-body text-muted">
            {dashboardQuery.error instanceof Error
              ? dashboardQuery.error.message
              : "This dashboard could not be loaded for your account."}
          </p>
          <Button type="button" variant="secondary" size="sm" className="mt-ds-4" onClick={handleBack}>
            Back to dashboards
          </Button>
        </Card>
      </div>
    );
  }

  const widgets = widgetsQuery.data ?? [];
  const isHmiDashboard = getDashboardLayoutKind(dashboard.layout) === "hmi_canvas";

  return (
    <>
      {canEditDashboard ? (
        <DashboardMetaModal
          state={editor}
          saving={updateMutation.isPending}
          error={editorError}
          availableUsers={assignableUsers}
          onClose={() => {
            if (!updateMutation.isPending) {
              setEditor({ open: false });
              setEditorError(null);
            }
          }}
          onChange={(patch) => {
            if (!editor.open) {
              return;
            }
            setEditor({
              ...editor,
              ...patch,
              assignedUserIds:
                patch.isGlobal === true ? [] : (patch.assignedUserIds ?? editor.assignedUserIds),
            });
          }}
          onToggleAssignment={(userId) => {
            if (!editor.open) {
              return;
            }
            const assignedUserIds = editor.assignedUserIds.includes(userId)
              ? editor.assignedUserIds.filter((id) => id !== userId)
              : [...editor.assignedUserIds, userId];
            setEditor({ ...editor, assignedUserIds });
          }}
          onSubmit={() => void submitEditor()}
        />
      ) : null}

      {isHmiDashboard ? (
        <HmiDashboardPage
          dashboard={dashboard}
          tenantId={tenantId}
          canEditDashboard={canEditDashboard}
          onBack={handleBack}
        />
      ) : widgetsQuery.isLoading ? (
        <div className="flex min-h-[24rem] items-center justify-center gap-ds-2 text-caption text-muted">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading canvas...
        </div>
      ) : widgetsQuery.isError ? (
        <Card className="m-ds-6 p-ds-4">
          <p className="text-caption text-error" role="alert">
            {widgetsQuery.error instanceof Error ? widgetsQuery.error.message : "Could not load widgets."}
          </p>
        </Card>
      ) : (
        <DashboardCanvasContent
          tenantId={tenantId}
          dashboard={dashboard}
          widgets={widgets}
          activeAlertFingerprints={activeAlertFingerprints}
          canEditDashboard={canEditDashboard}
          onBack={handleBack}
          onEditDetails={() => {
            if (!canEditDashboard) {
              return;
            }
            setEditorError(null);
            setEditor(buildEditEditorState(dashboard));
          }}
        />
      )}
    </>
  );
}
